import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Shield, CalendarCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { insertLeadSchema } from "@shared/schema";

const formSchema = insertLeadSchema.extend({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(10, "Valid phone number is required"),
  message: z.string().optional(),
});

interface LeadCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  propertyTitle: string;
  leadType: "tour" | "contact";
  property?: {
    id: string;
    title: string;
    address: string;
    city: string;
    state: string;
    price: number | string;
    bedrooms: number;
    bathrooms: number;
  };
}

export default function LeadCaptureModal({
  isOpen,
  onClose,
  propertyId,
  propertyTitle,
  leadType,
  property,
}: LeadCaptureModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      propertyId,
      agentId: "agent1", // Default agent
      leadType,
      preferredTime: "",
      message: "",
      status: "new",
    },
  });

  const createLeadMutation = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      const response = await apiRequest("POST", "/api/leads", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: leadType === "tour" 
          ? "Your tour has been scheduled! We'll contact you within 24 hours."
          : "Your message has been sent! We'll get back to you soon.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
      form.reset();
      onClose();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    createLeadMutation.mutate(data);
  };

  const title = leadType === "tour" ? "Schedule Your Private Tour" : "Contact Our Agent";
  const description = leadType === "tour" 
    ? "Get exclusive access to this luxury waterfront property"
    : "Get more information about this property";

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (!numPrice) return 'Price on request';
    if (numPrice >= 1000000) {
      return `$${(numPrice / 1000000).toFixed(1)}M`;
    } else if (numPrice >= 1000) {
      return `$${(numPrice / 1000).toFixed(0)}K`;
    }
    return `$${numPrice.toLocaleString()}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000]" style={{ zIndex: 10000 }}>
      {/* Light backdrop */}
      <div className="absolute inset-0 bg-black/10" onClick={onClose} />
      
      {/* Form Overlay - positioned near map popup area */}
      <div className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-xl border max-w-xs w-full mx-4">
        <div className="p-3">
          <div className="text-center">
            <div className="w-8 h-8 bg-luxury-gold rounded-full flex items-center justify-center mx-auto mb-2">
              <CalendarCheck className="w-4 h-4 text-deep-navy" />
            </div>
            <h2 className="font-playfair text-lg text-deep-navy mb-1">
              {title}
            </h2>
            <p className="text-xs text-gray-600 mb-2">{description}</p>
            
            {/* Compact Property Information */}
            {property && (
              <div className="bg-gray-50 rounded p-2 mb-3 text-center">
                <h4 className="font-semibold text-sm text-deep-navy mb-1">
                  {formatPrice(property.price)}
                </h4>
                <p className="text-xs text-gray-600">
                  {property.bedrooms || 'N/A'} bed, {property.bathrooms || 'N/A'} bath
                </p>
              </div>
            )}
          </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
            <div className="grid grid-cols-2 gap-1">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input placeholder="First Name" className="h-8 text-xs" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input placeholder="Last Name" className="h-8 text-xs" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input type="email" placeholder="Email" className="h-8 text-xs" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input type="tel" placeholder="Phone" className="h-8 text-xs" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {leadType === "tour" && (
              <FormField
                control={form.control}
                name="preferredTime"
                render={({ field }) => (
                  <FormItem>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="Time" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="morning">Morning</SelectItem>
                        <SelectItem value="afternoon">Afternoon</SelectItem>
                        <SelectItem value="evening">Evening</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="flex space-x-1 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1 h-8 text-xs"
                disabled={createLeadMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-ocean-blue hover:bg-bright-blue h-8 text-xs"
                disabled={createLeadMutation.isPending}
              >
                {createLeadMutation.isPending ? "..." : leadType === "tour" ? "Schedule" : "Send"}
              </Button>
            </div>
          </form>
        </Form>

          <div className="text-center pt-2">
            <div className="flex items-center justify-center text-xs text-gray-500">
              <Shield className="w-3 h-3 text-mint-green mr-1" />
              Your information is secure
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
